import {
  Avatar,
  Button,
  Caption1,
  Divider,
  Link,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Text,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowForward20Regular,
  ArrowReply20Regular,
  ArrowReplyAll20Regular,
  Archive20Regular,
  Delete20Regular,
  Document20Regular,
  Flag20Filled,
  Flag20Regular,
  FolderArrowRight20Regular,
  Mail20Regular,
} from '@fluentui/react-icons';
import { brandName, cidReferences, type Message } from '@gandhi/mail-protocol';
import { useState, type ReactElement } from 'react';
import type { MailApiClient } from '../api/client.js';
import { fullDate, formatBytes, initialsOf } from '../format.js';
import { folderTree, type FolderNode } from '../state/folders.js';
import { useController, useMail } from '../state/context.js';
import { MessageBody } from './MessageBody.js';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    height: '100%',
    padding: '32px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '16px 20px 4px',
    background: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  people: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  meta: { color: tokens.colorNeutralForeground3 },
  earlier: { padding: '8px 20px' },
  message: { padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: '8px' },
  from: { display: 'flex', alignItems: 'center', gap: '10px' },
  fromText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  attachments: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
  },
  cardText: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  // Divider grows by default, which in a column flex pushes the message to the bottom.
  divider: { flexGrow: 0 },
  toolbar: { flexWrap: 'wrap' },
});

export function ReadingPane(props: { api: MailApiClient }): ReactElement {
  const styles = useStyles();
  const controller = useController();
  const thread = useMail((s) => s.activeThread);
  const opening = useMail((s) => s.activeThreadId !== null && s.activeThread === null);
  const folders = useMail((s) => s.folders);
  const [showEarlier, setShowEarlier] = useState(false);

  if (thread === null) {
    return (
      <section className={styles.root} aria-label="Reading pane">
        <div className={styles.empty} {...(opening ? { role: 'status' } : {})}>
          {opening ? (
            <Spinner label="Opening the conversation…" />
          ) : (
            <>
              <Text weight="semibold">Select a message to read it.</Text>
              <Caption1>Nothing is open in this pane.</Caption1>
            </>
          )}
        </div>
      </section>
    );
  }

  const latest = thread.messages[thread.messages.length - 1];
  const earlier = thread.messages.slice(0, -1);
  const archive = folders.find((f) => f.role === 'archive');
  const trash = folders.find((f) => f.role === 'trash');
  const move = (folderId: string): void => {
    void controller.setThreadFlags(thread.id, { folderId });
  };

  return (
    <section className={styles.root} aria-label="Reading pane" data-brand={thread.brandId}>
      <header className={styles.header}>
        <Text as="h2" size={500} weight="semibold">
          {thread.subject || '(no subject)'}
        </Text>
        <div className={styles.people}>
          {thread.participants.map((person, index) => (
            <Avatar
              key={`${person.address}-${index}`}
              size={24}
              name={person.name ?? person.address}
              initials={initialsOf(person.name, person.address)}
            />
          ))}
          <Caption1 className={styles.meta}>
            {brandName(thread.brandId)} conversation · {thread.messageCount} message
            {thread.messageCount === 1 ? '' : 's'} · {fullDate(thread.latestAt)}
          </Caption1>
        </div>

        {latest !== undefined && (
          <Toolbar className={styles.toolbar} aria-label="Conversation actions" size="small">
            <ToolbarButton
              icon={<ArrowReply20Regular />}
              onClick={() => controller.reply(latest, false)}
            >
              Reply
            </ToolbarButton>
            <ToolbarButton
              icon={<ArrowReplyAll20Regular />}
              onClick={() => controller.reply(latest, true)}
            >
              Reply all
            </ToolbarButton>
            <ToolbarButton
              icon={<ArrowForward20Regular />}
              onClick={() => controller.forward(latest)}
            >
              Forward
            </ToolbarButton>
            <ToolbarDivider />
            {archive !== undefined && (
              <ToolbarButton
                icon={<Archive20Regular />}
                aria-label="Archive"
                onClick={() => move(archive.id)}
              >
                Archive
              </ToolbarButton>
            )}
            {trash !== undefined && (
              <ToolbarButton
                icon={<Delete20Regular />}
                aria-label="Delete"
                onClick={() => move(trash.id)}
              >
                Delete
              </ToolbarButton>
            )}
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <ToolbarButton icon={<FolderArrowRight20Regular />}>Move to</ToolbarButton>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  {folderTree(folders).map((node) => (
                    <MoveItems key={node.folder.id} node={node} depth={0} onMove={move} />
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
            <ToolbarDivider />
            <ToolbarButton
              icon={<Mail20Regular />}
              onClick={() => void controller.setThreadFlags(thread.id, { unread: true })}
            >
              Mark unread
            </ToolbarButton>
            <ToolbarButton
              icon={thread.flagged ? <Flag20Filled /> : <Flag20Regular />}
              aria-pressed={thread.flagged}
              onClick={() =>
                void controller.setThreadFlags(thread.id, { flagged: !thread.flagged })
              }
            >
              {thread.flagged ? 'Unflag' : 'Flag'}
            </ToolbarButton>
          </Toolbar>
        )}
      </header>

      {earlier.length > 0 && (
        <div className={styles.earlier}>
          <Button
            appearance="subtle"
            aria-expanded={showEarlier}
            onClick={() => setShowEarlier(!showEarlier)}
          >
            {showEarlier ? 'Hide' : 'Show'} {earlier.length} earlier message
            {earlier.length === 1 ? '' : 's'}
          </Button>
        </div>
      )}

      {(showEarlier ? thread.messages : thread.messages.slice(-1)).map((message) => (
        <MessageCard key={message.id} api={props.api} message={message} />
      ))}
    </section>
  );
}

function MoveItems(props: {
  node: FolderNode;
  depth: number;
  onMove: (folderId: string) => void;
}): ReactElement {
  const { node, depth, onMove } = props;
  return (
    <>
      <MenuItem
        style={{ paddingInlineStart: `${12 + depth * 16}px` }}
        onClick={() => onMove(node.folder.id)}
      >
        {node.folder.name}
      </MenuItem>
      {node.children.map((child) => (
        <MoveItems key={child.folder.id} node={child} depth={depth + 1} onMove={onMove} />
      ))}
    </>
  );
}

function MessageCard(props: { api: MailApiClient; message: Message }): ReactElement {
  const styles = useStyles();
  const controller = useController();
  const { api, message } = props;
  // A part shown inside the body is not also a file to download.
  const inline = new Set(cidReferences(message.bodyHtml));
  const listed = message.attachments.filter((a) => a.cid === undefined || !inline.has(a.cid));
  return (
    <>
      <Divider className={styles.divider} />
      <article className={styles.message} aria-label={`Message from ${message.from.address}`}>
        <div className={styles.from}>
          <Avatar
            size={32}
            name={message.from.name ?? message.from.address}
            initials={initialsOf(message.from.name, message.from.address)}
          />
          <div className={styles.fromText}>
            <Text weight="semibold">{message.from.name ?? message.from.address}</Text>
            <Caption1 className={styles.meta}>
              {message.from.address} → {message.to.map((t) => t.address).join(', ')} ·{' '}
              {fullDate(message.sentAt)}
            </Caption1>
          </div>
        </div>
        <MessageBody
          message={message}
          api={api}
          onCompose={(to, subject) => controller.composeNew({ to, subject })}
        />
        {listed.length > 0 && (
          <div className={styles.attachments} aria-label="Attachments">
            {listed.map((attachment) => (
              <div key={attachment.id} className={styles.card}>
                <Document20Regular aria-hidden="true" />
                <div className={styles.cardText}>
                  <Link
                    href={api.attachmentUrl(message.mailboxId, message.id, attachment.id)}
                    download={attachment.name}
                  >
                    {attachment.name}
                  </Link>
                  <Caption1 className={styles.meta}>{formatBytes(attachment.size)}</Caption1>
                </div>
              </div>
            ))}
          </div>
        )}
      </article>
    </>
  );
}
